/**
 * WooCommerce Checkout Block Integration Constants
 *
 * Shared constants and utilities for registering blocks as inner blocks
 * within the WooCommerce checkout block.
 */

/**
 * Parent blocks where custom blocks can be inserted in the WooCommerce checkout.
 * These are the standard checkout areas that accept inner blocks.
 */
export const WC_CHECKOUT_ALLOWED_PARENTS = [
	'woocommerce/checkout-fields-block',
	'woocommerce/checkout-totals-block',
	'woocommerce/checkout-contact-information-block',
	'woocommerce/checkout-shipping-address-block',
	'woocommerce/checkout-billing-address-block',
	'woocommerce/checkout-shipping-method-block',
	'woocommerce/checkout-order-summary-totals-block',
] as const;

export type WcCheckoutAllowedParent = typeof WC_CHECKOUT_ALLOWED_PARENTS[number];

/**
 * Register a block as an allowed inner block for WooCommerce checkout areas.
 *
 * @param blockName - The full block name (e.g., 'leat/spend-rules-checkout')
 * @param filterNamespace - A unique namespace for the filter (e.g., 'leat-spend-rules-checkout')
 */
export function registerAsCheckoutInnerBlock(blockName: string, filterNamespace: string): void {
	if (typeof window.wc?.blocksCheckout?.registerCheckoutFilters !== 'function') {
		return;
	}

	window.wc.blocksCheckout.registerCheckoutFilters(filterNamespace, {
		additionalCartCheckoutInnerBlockTypes: (
			value: unknown,
			_extensions: unknown,
			args?: unknown
		): unknown => {
			const valueArray = value as string[];
			const argsObj = args as { block?: string } | undefined;

			// Add our block to the list of allowed inner blocks for each parent
			if (argsObj?.block && (WC_CHECKOUT_ALLOWED_PARENTS as readonly string[]).includes(argsObj.block)) {
				return [...valueArray, blockName];
			}
			return value;
		},
	});
}
